/**
 * Klien API tunggal.
 *
 * Kontrak backend (lihat apps/api/src/core/response.ts):
 *   sukses : { success: true,  data, meta? }
 *   gagal  : { success: false, error: { code, message, details? } }
 *
 * Aturan penting:
 *  - Token akses disimpan di memori + sessionStorage (kunci 'yys_token').
 *  - Query SELALU dibangun dengan URLSearchParams, tidak pernah disambung manual.
 *  - HTTP 401 hanya memicu logout + redirect bila permintaan sebelumnya MEMANG
 *    membawa token (mencegah loop reload pada halaman login).
 */

export const KUNCI_TOKEN = 'yys_token'
export const BASE_API = (import.meta.env.VITE_API_BASE ?? '/api').replace(/\/+$/, '')

export type MetodeHttp = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export type NilaiQuery =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>

export type ObyekQuery = Record<string, NilaiQuery>

export interface MetaPaginasi {
  page: number
  perPage: number
  total: number
  totalPages: number
}

export interface AmplopSukses<T> {
  success: true
  data: T
  meta?: MetaPaginasi & Record<string, unknown>
}

export interface AmplopGagal {
  success: false
  error: { code: string; message: string; details?: unknown }
}

/** Bentuk amplop "longgar" untuk pembacaan respons yang belum tentu valid. */
export interface AmplopLonggar<T> {
  success?: boolean
  data?: T
  meta?: MetaPaginasi & Record<string, unknown>
  error?: { code: string; message: string; details?: unknown }
}

export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: unknown

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.code = code
    this.status = status
    this.details = details
  }

  /** Rincian validasi (bila ada) dalam bentuk daftar pesan yang dapat ditampilkan. */
  get daftarMasalah(): Array<{ path: string; message: string }> {
    const d = this.details
    if (Array.isArray(d)) {
      return d
        .filter((x): x is { path?: unknown; message?: unknown } => typeof x === 'object' && x !== null)
        .map((x) => ({ path: String(x.path ?? ''), message: String(x.message ?? '') }))
    }
    return []
  }
}

export function pesanKesalahan(e: unknown): string {
  if (e instanceof ApiError) return e.message
  if (e instanceof Error) return e.message
  return 'Terjadi kesalahan yang tidak diketahui.'
}

export function kodeKesalahan(e: unknown): string {
  return e instanceof ApiError ? e.code : 'UNKNOWN'
}

// --------------------------------------------------------------- Penyimpanan token
let tokenMemori: string | null = bacaTokenAwal()

function bacaTokenAwal(): string | null {
  try {
    return sessionStorage.getItem(KUNCI_TOKEN)
  } catch {
    return null
  }
}

export function getToken(): string | null {
  return tokenMemori
}

export function setToken(token: string | null): void {
  tokenMemori = token
  try {
    if (token) sessionStorage.setItem(KUNCI_TOKEN, token)
    else sessionStorage.removeItem(KUNCI_TOKEN)
  } catch {
    /* mode privat: token tetap hidup di memori untuk sesi ini */
  }
}

export function hapusToken(): void {
  setToken(null)
}

// ------------------------------------------------------ Penanganan 401 terpusat
type PenanganTidakSah = () => void
let penanganTidakSah: PenanganTidakSah | null = null

export function pasangPenanganTidakSah(fn: PenanganTidakSah | null): void {
  penanganTidakSah = fn
}

// ------------------------------------------------------------------ URL & query
export function bangunQuery(query?: ObyekQuery): string {
  if (!query) return ''
  const params = new URLSearchParams()
  for (const [kunci, nilai] of Object.entries(query)) {
    if (nilai === undefined || nilai === null || nilai === '') continue
    if (Array.isArray(nilai)) {
      for (const n of nilai) {
        if (n === undefined || n === null || n === '') continue
        params.append(kunci, String(n))
      }
    } else {
      params.set(kunci, String(nilai))
    }
  }
  const hasil = params.toString()
  return hasil ? `?${hasil}` : ''
}

export function urlApi(path: string, query?: ObyekQuery): string {
  const bersih = path.startsWith('/') ? path : `/${path}`
  const lengkap = bersih.startsWith(BASE_API) ? bersih : `${BASE_API}${bersih}`
  return `${lengkap}${bangunQuery(query)}`
}

export interface OpsiKirim {
  query?: ObyekQuery
  body?: unknown
  signal?: AbortSignal
  idempotencyKey?: string
}

export interface Hasil<T> {
  data: T
  meta?: (MetaPaginasi & Record<string, unknown>) | undefined
}

// ---------------------------------------------------------------- Inti permintaan
export async function kirim<T>(
  method: MetodeHttp,
  path: string,
  opsi: OpsiKirim = {},
): Promise<Hasil<T>> {
  const adaTokenSebelumnya = Boolean(tokenMemori)
  const header: Record<string, string> = { Accept: 'application/json' }
  if (opsi.body !== undefined) header['Content-Type'] = 'application/json'
  if (tokenMemori) header.Authorization = `Bearer ${tokenMemori}`
  if (opsi.idempotencyKey) header['X-Idempotency-Key'] = opsi.idempotencyKey

  let res: Response
  try {
    res = await fetch(urlApi(path, opsi.query), {
      method,
      headers: header,
      body: opsi.body === undefined ? undefined : JSON.stringify(opsi.body),
      signal: opsi.signal,
      credentials: 'include',
    })
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw new ApiError(
      'JARINGAN_GAGAL',
      'Tidak dapat menghubungi server. Periksa koneksi Anda atau pastikan API sedang berjalan.',
      0,
    )
  }

  if (res.status === 204) return { data: undefined as T }

  let isi: unknown = null
  const teks = await res.text()
  if (teks) {
    try {
      isi = JSON.parse(teks)
    } catch {
      isi = null
    }
  }

  const amplop = (typeof isi === 'object' && isi !== null ? isi : null) as AmplopLonggar<T> | null

  if (res.status === 401 && adaTokenSebelumnya) {
    hapusToken()
    penanganTidakSah?.()
    throw new ApiError(
      amplop?.success === false ? amplop.error?.code ?? 'UNAUTHENTICATED' : 'UNAUTHENTICATED',
      amplop?.success === false
        ? amplop.error?.message ?? 'Sesi Anda telah berakhir.'
        : 'Sesi Anda telah berakhir. Silakan masuk kembali.',
      401,
    )
  }

  const gagalSecaraIsi = amplop !== null && amplop.success === false

  if (!res.ok || gagalSecaraIsi) {
    const kode = gagalSecaraIsi
      ? amplop.error?.code ?? `HTTP_${res.status}`
      : `HTTP_${res.status}`
    const pesan = gagalSecaraIsi
      ? amplop.error?.message ?? `Permintaan gagal (HTTP ${res.status}).`
      : pesanBawaanStatus(res.status)
    throw new ApiError(kode, pesan, res.status, gagalSecaraIsi ? amplop.error?.details : undefined)
  }

  // Respons yang SUDAH beramplop: { success: true, data, meta }
  if (amplop !== null && amplop.success === true) {
    return {
      data: amplop.data as T,
      meta: amplop.meta as (MetaPaginasi & Record<string, unknown>) | undefined,
    }
  }

  // Respons tanpa amplop (mis. GET /api/health versi lama)
  return { data: isi as T }
}

function pesanBawaanStatus(status: number): string {
  switch (status) {
    case 400:
      return 'Permintaan tidak valid.'
    case 401:
      return 'Anda belum masuk atau sesi telah berakhir.'
    case 403:
      return 'Anda tidak berhak melakukan tindakan ini.'
    case 404:
      return 'Data tidak ditemukan.'
    case 409:
      return 'Terjadi konflik data.'
    case 413:
      return 'Ukuran data terlalu besar.'
    case 422:
      return 'Aturan bisnis menolak permintaan ini.'
    case 429:
      return 'Terlalu banyak permintaan. Coba beberapa saat lagi.'
    case 500:
      return 'Terjadi kesalahan pada server.'
    case 501:
      return 'Fitur ini belum diimplementasikan pada server.'
    case 503:
      return 'Layanan sedang tidak tersedia.'
    default:
      return `Permintaan gagal (HTTP ${status}).`
  }
}

// ----------------------------------------------------------------------- Helper
async function dapat<T>(path: string, query?: ObyekQuery, signal?: AbortSignal): Promise<T> {
  return (await kirim<T>('GET', path, { query, signal })).data
}

async function kirimBody<T>(
  method: MetodeHttp,
  path: string,
  body?: unknown,
  opsi: OpsiKirim = {},
): Promise<T> {
  return (await kirim<T>(method, path, { ...opsi, body })).data
}

/** Menormalkan payload daftar menjadi { items, meta }. */
export function normalisasiPaged<T>(hasil: Hasil<unknown>): { items: T[]; meta: MetaPaginasi | null } {
  const d = hasil.data
  if (Array.isArray(d)) return { items: d as T[], meta: (hasil.meta as MetaPaginasi) ?? null }
  if (d && typeof d === 'object') {
    const obj = d as Record<string, unknown>
    const kandidat = ['items', 'data', 'rows', 'daftar', 'records', 'list']
    for (const k of kandidat) {
      if (Array.isArray(obj[k])) {
        return { items: obj[k] as T[], meta: (hasil.meta as MetaPaginasi) ?? null }
      }
    }
  }
  return { items: [], meta: (hasil.meta as MetaPaginasi) ?? null }
}

async function getPaged<T>(
  path: string,
  query?: ObyekQuery,
  signal?: AbortSignal,
): Promise<{ items: T[]; meta: MetaPaginasi | null }> {
  const hasil = await kirim<unknown>('GET', path, { query, signal })
  return normalisasiPaged<T>(hasil)
}

/** Unduh berkas ekspor (PDF/XLSX/CSV) memakai header otorisasi. */
export async function downloadBerkas(
  path: string,
  namaFile: string,
  query?: ObyekQuery,
): Promise<void> {
  const header: Record<string, string> = {}
  if (tokenMemori) header.Authorization = `Bearer ${tokenMemori}`

  let res: Response
  try {
    res = await fetch(urlApi(path, query), { headers: header, credentials: 'include' })
  } catch {
    throw new ApiError(
      'JARINGAN_GAGAL',
      'Tidak dapat mengunduh berkas. Periksa koneksi Anda.',
      0,
    )
  }

  if (!res.ok) {
    let pesan = pesanBawaanStatus(res.status)
    let kode = `HTTP_${res.status}`
    try {
      const isi = (await res.json()) as Partial<AmplopGagal>
      if (isi && isi.success === false && isi.error) {
        pesan = isi.error.message
        kode = isi.error.code
      }
    } catch {
      /* biarkan pesan bawaan */
    }
    throw new ApiError(kode, pesan, res.status)
  }

  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = namaFile
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}

/** Unggah berkas (multipart/form-data) — TIDAK memakai JSON.stringify. */
export async function unggah<T>(path: string, formData: FormData, method: MetodeHttp = 'POST'): Promise<T> {
  const adaTokenSebelumnya = Boolean(tokenMemori)
  const header: Record<string, string> = {}
  if (tokenMemori) header.Authorization = `Bearer ${tokenMemori}`

  let res: Response
  try {
    res = await fetch(urlApi(path), { method, headers: header, body: formData, credentials: 'include' })
  } catch {
    throw new ApiError('JARINGAN_GAGAL', 'Tidak dapat mengunggah berkas. Periksa koneksi Anda.', 0)
  }

  let isi: unknown = null
  const teks = await res.text()
  if (teks) {
    try {
      isi = JSON.parse(teks)
    } catch {
      isi = null
    }
  }
  const amplop = (typeof isi === 'object' && isi !== null ? isi : null) as AmplopLonggar<T> | null

  if (res.status === 401 && adaTokenSebelumnya) {
    hapusToken()
    penanganTidakSah?.()
  }
  if (!res.ok || (amplop !== null && amplop.success === false)) {
    const kode = amplop && amplop.success === false ? amplop.error?.code ?? `HTTP_${res.status}` : `HTTP_${res.status}`
    const pesan =
      amplop && amplop.success === false ? amplop.error?.message ?? pesanBawaanStatus(res.status) : pesanBawaanStatus(res.status)
    throw new ApiError(kode, pesan, res.status)
  }
  if (amplop !== null && amplop.success === true) return amplop.data as T
  return isi as T
}

export const api = {
  get: dapat,
  getLengkap: <T,>(path: string, query?: ObyekQuery, signal?: AbortSignal) =>
    kirim<T>('GET', path, { query, signal }),
  getPaged,
  post: <T,>(path: string, body?: unknown, opsi?: OpsiKirim) => kirimBody<T>('POST', path, body, opsi),
  patch: <T,>(path: string, body?: unknown, opsi?: OpsiKirim) =>
    kirimBody<T>('PATCH', path, body, opsi),
  put: <T,>(path: string, body?: unknown, opsi?: OpsiKirim) => kirimBody<T>('PUT', path, body, opsi),
  delete: <T,>(path: string, body?: unknown) => kirimBody<T>('DELETE', path, body),
  unggah,
}

export function buatIdempotencyKey(): string {
  try {
    return crypto.randomUUID()
  } catch {
    return `idem-${Date.now()}-${Math.random().toString(16).slice(2)}`
  }
}
